import React from 'react';
import { useController, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';

import {
  Box,
  Card,
  Grid,
  Alert,
  Button,
  SvgIcon,
  AlertTitle,
  Typography,
  CardContent,
  CardActions,
} from '@mui/material';

import {
  Loading,
  TextField,
  AlertDialog,
  SnakebarToaster,
  SimpleRadioGroupField,
  AutoCompleteSingleSelect,
} from '@/components';
import { CROSSING_DIRECTION_COUNTRY } from '@/features/shared/constants';
import {
  isBorderCrossingRoRo,
  OPEN_CUSTOMS_AND_CROSSING,
  isBorderCrossingRoRoEmptyV2,
} from '@/features/shared/helpers/typologies';
import { useCountriesOptions } from '@/features/shared/hooks';
import { OrangeClockIcon, SendPlaneLineIcon, CloseIcon } from '@/utils/iconSVG';

import { useCancelPreFolderQualification } from '../hooks';
import {
  stylesErrorMessageBox,
  stylesCloseModalButton,
  stylesErrorMessagesText,
  stylesErrorMessagesTitle,
  stylesCreateNewFolderCard,
  stylesAutoCompleteSingleSelect,
  stylesCreateNewFolderCardContent,
  stylesCreateNewRequestFormSubtitle,
} from './PreFolderQualificationWithRORO.styles';
import { usePreFolderQualificationWithRORO } from './hooks';
import { REGISTER_NEW_FOLDER_FORM } from './types';

export const PreFolderQualificationWithRORO: React.FunctionComponent = () => {
  const { t } = useTranslation('borderPassEasyWithRORO_prefolderQualification');
  const { countriesOptions, isCountriesReferentialLoading } = useCountriesOptions();

  const {
    isFolderStatusAndItsTypologyDataLoading,
    isAddNewFolderByTypologyPending,
    isCreateNewFolderInError,
    formState: { errors },
    direction,
    control,
    register,
    handleCloseToaster,
    handleSubmitNewFolder,
  } = usePreFolderQualificationWithRORO();

  const {
    isCancelPreFolderAlertDialogOpen,
    isCancelPreFolderPending,
    cancelPreFolderState,
    handleCancelPreFolder,
    handleAgreeCancelPreFolder,
    handleOnDisagreeCancelPreFolder,
    handleCloseCanclPreFolderToaster,
  } = useCancelPreFolderQualification();

  const {
    field: { value: vehicleNationalityValue, onChange: onChangeVehicleNationality },
  } = useController({
    name: REGISTER_NEW_FOLDER_FORM.vehicleNationality,
    control,
  });

  const { folderTypologyId, containsEmptyPackages } = useWatch({ control }) as {
    folderTypologyId: OPEN_CUSTOMS_AND_CROSSING;
    containsEmptyPackages: string;
  };

  if (isFolderStatusAndItsTypologyDataLoading || isCountriesReferentialLoading) {
    return <Loading />;
  }

  return (
    <>
      {isCreateNewFolderInError && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12} lg={6}>
            <Alert severity='error' sx={stylesErrorMessageBox}>
              <Box>
                <Typography sx={stylesErrorMessagesTitle}>
                  {t('borderPassEasyWithRORO.prefolderQualification.createFolder.errorMessage.title')}
                </Typography>
                <Typography sx={stylesErrorMessagesText}>
                  <ReactMarkdown>
                    {t('borderPassEasyWithRORO.prefolderQualification.createFolder.errorMessage.text')}
                  </ReactMarkdown>
                </Typography>
              </Box>
              <Button variant='text' color='error' sx={stylesCloseModalButton} onClick={handleCloseToaster}>
                <SvgIcon color='error' component={CloseIcon} />
              </Button>
            </Alert>
          </Grid>
        </Grid>
      )}

      <Card variant='outlined' sx={stylesCreateNewFolderCard}>
        <CardContent sx={stylesCreateNewFolderCardContent}>
          <Grid container spacing={2} item xs={12} sm={12} md={12} lg={6}>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <TextField
                labelSx={{ pb: 1 }}
                error={errors.internalClientReference}
                id={REGISTER_NEW_FOLDER_FORM.internalClientReference}
                registration={register(REGISTER_NEW_FOLDER_FORM.internalClientReference)}
                label={t('borderPassEasyWithRORO.prefolderQualification.form.internalClientReference.label')}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <SimpleRadioGroupField
                isRequired
                control={control}
                labelSx={{ pb: 1 }}
                error={errors.folderTypologyId}
                sx={{ width: '100%', mb: 0, mt: 0, zIndex: 1 }}
                name={REGISTER_NEW_FOLDER_FORM.folderTypologyId}
                title={t('borderPassEasyWithRORO.prefolderQualification.form.folderTypologyId.label')}
                options={[
                  {
                    label: (
                      <Box component='span' sx={{ fontWeight: 'bold' }}>
                        {t('borderPassEasyWithRORO.prefolderQualification.form.folderTypologyId.full.label')}
                      </Box>
                    ),
                    value: OPEN_CUSTOMS_AND_CROSSING.BORDER_CROSSING_RORO,
                  },
                  {
                    label: (
                      <Box component='span' sx={{ fontWeight: 'bold' }}>
                        {t('borderPassEasyWithRORO.prefolderQualification.form.folderTypologyId.empty.label')}
                      </Box>
                    ),
                    value: OPEN_CUSTOMS_AND_CROSSING.BORDER_CROSSING_RORO_EMPTY_V2,
                  },
                ]}
              />
            </Grid>
            {isBorderCrossingRoRo({ folderTypologyId }) && (
              <Grid item xs={12}>
                <Grid container spacing={2} item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <Typography
                      component='div'
                      variant='bodyMDMedium'
                      sx={{ ...stylesCreateNewRequestFormSubtitle, pb: 0 }}
                    >
                      {t('borderPassEasyWithRORO.prefolderQualification.form.transportInformation.label')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    <AutoCompleteSingleSelect
                      isRequired
                      multiple={false}
                      labelSx={{ pb: 1 }}
                      key={folderTypologyId}
                      options={countriesOptions}
                      error={errors.vehicleNationality}
                      sx={stylesAutoCompleteSingleSelect}
                      id={REGISTER_NEW_FOLDER_FORM.vehicleNationality}
                      getOptionLabel={(option) => option?.label}
                      value={countriesOptions.find((item) => item.value === vehicleNationalityValue) ?? null}
                      label={t('borderPassEasyWithRORO.prefolderQualification.form.vehicleNationality.label')}
                      onChange={(_, data) => onChangeVehicleNationality?.(data?.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    {direction === CROSSING_DIRECTION_COUNTRY.EUUK ? (
                      <TextField
                        isRequired
                        labelSx={{ pb: 1 }}
                        error={errors.haulierGbEoriNumber}
                        id={REGISTER_NEW_FOLDER_FORM.haulierGbEoriNumber}
                        registration={register(REGISTER_NEW_FOLDER_FORM.haulierGbEoriNumber)}
                        label={t('borderPassEasyWithRORO.prefolderQualification.form.haulierGbEoriNumber.label')}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\s+/g, '').replace('-', '').replace('_', '');
                          e.target.value = value.toUpperCase();
                          register(REGISTER_NEW_FOLDER_FORM.haulierGbEoriNumber).onChange(e);
                        }}
                      />
                    ) : (
                      <TextField
                        isRequired
                        labelSx={{ pb: 1 }}
                        error={errors.haulierEuEoriNumber}
                        id={REGISTER_NEW_FOLDER_FORM.haulierEuEoriNumber}
                        registration={register(REGISTER_NEW_FOLDER_FORM.haulierEuEoriNumber)}
                        label={t('borderPassEasyWithRORO.prefolderQualification.form.haulierEuEoriNumber.label')}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\s+/g, '').replace('-', '').replace('_', '');
                          e.target.value = value.toUpperCase();
                          register(REGISTER_NEW_FOLDER_FORM.haulierEuEoriNumber).onChange(e);
                        }}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )}
            {isBorderCrossingRoRoEmptyV2({ folderTypologyId }) && (
              <>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <SimpleRadioGroupField
                    isRequired
                    control={control}
                    labelSx={{ pb: 1 }}
                    error={errors.containsEmptyPackages}
                    sx={{ width: '100%', mb: 0, mt: 0, zIndex: 1 }}
                    name={REGISTER_NEW_FOLDER_FORM.containsEmptyPackages}
                    title={t('borderPassEasyWithRORO.prefolderQualification.form.containsEmptyPackages.label')}
                    options={[
                      {
                        label: (
                          <Box component='span' sx={{ fontWeight: 'bold' }}>
                            {t('borderPassEasyWithRORO.prefolderQualification.form.containsEmptyPackages.yes.label')}
                          </Box>
                        ),
                        value: true,
                      },
                      {
                        label: (
                          <Box component='span' sx={{ fontWeight: 'bold' }}>
                            {t('borderPassEasyWithRORO.prefolderQualification.form.containsEmptyPackages.non.label')}
                          </Box>
                        ),
                        value: false,
                      },
                    ]}
                  />
                </Grid>
                {containsEmptyPackages === 'true' && (
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    <Alert severity='warning' icon={<SvgIcon component={OrangeClockIcon} />}>
                      <AlertTitle>
                        {t('borderPassEasyWithRORO.prefolderQualification.form.containsEmptyPackages.warning.title')}
                      </AlertTitle>
                      <Box component='span' color='grey.800'>
                        {t('borderPassEasyWithRORO.prefolderQualification.form.containsEmptyPackages.warning.content')}
                      </Box>
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <Alert severity='warning' icon={<SvgIcon component={OrangeClockIcon} />}>
                    <AlertTitle>
                      {t(
                        'borderPassEasyWithRORO.prefolderQualification.form.folderTypologyId.empty.ensGeneration.warning.title',
                      )}
                    </AlertTitle>
                    <Box component='span' color='grey.800'>
                      {t(
                        'borderPassEasyWithRORO.prefolderQualification.form.folderTypologyId.empty.ensGeneration.warning.content',
                      )}
                    </Box>
                  </Alert>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
        <CardActions sx={{ pb: 3, display: 'flex', justifyContent: 'end' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={6} display='flex' justifyContent='space-between'>
              <Button
                color='error'
                variant='contained'
                disabled={isCancelPreFolderPending || isAddNewFolderByTypologyPending}
                onClick={handleCancelPreFolder}
              >
                {t('borderPassEasyWithRORO.prefolderQualification.form.cancelPreQualification.label')}
              </Button>
              <Button
                color='success'
                variant='contained'
                startIcon={<SendPlaneLineIcon />}
                disabled={isCancelPreFolderPending || isAddNewFolderByTypologyPending}
                onClick={handleSubmitNewFolder}
              >
                {t('borderPassEasyWithRORO.prefolderQualification.form.validateAndSend.label')}
              </Button>
            </Grid>
          </Grid>
        </CardActions>
      </Card>

      <AlertDialog
        agreeButtonColor='error'
        open={isCancelPreFolderAlertDialogOpen}
        title={t('borderPassEasyWithRORO.prefolderQualification.cancelPreFolder.title.label')}
        content={t('borderPassEasyWithRORO.prefolderQualification.cancelPreFolder.content.label')}
        disagreeMessage={t('borderPassEasyWithRORO.prefolderQualification.cancelPreFolder.cancel.label')}
        agreeMessage={t('borderPassEasyWithRORO.prefolderQualification.cancelPreFolder.agreeMessage.label')}
        onDisagree={handleOnDisagreeCancelPreFolder}
        onAgree={handleAgreeCancelPreFolder}
      />

      {cancelPreFolderState.isToasterOpen && (
        <SnakebarToaster
          isOpen
          autoHideDuration={5000}
          content={cancelPreFolderState.content}
          severity={cancelPreFolderState.severity}
          onClose={handleCloseCanclPreFolderToaster}
        />
      )}
    </>
  );
};
